import { useQuery } from '@tanstack/react-query';
import type {
  DeadlineListResponse,
  FilingDeadlineListParams,
  CreateManualDeadlineRequest,
  CreateManualDeadlineResponse,
  FinanceHealthDto,
  ObligationsResponse,
  RecognizedReportTypeDto,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, type ApiErrorBody } from './apiFetch';
import type { CurrentRead } from '@signalvote/data';
import { financeReadKeys, queryKeys } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { FINANCE_STALE_TIME } from './queryUtils';

// Re-export types for consumer convenience
export type {
  DeadlineListResponse,
  FilingDeadlineListParams,
  FinanceHealthDto,
  ObligationsResponse,
};

// ============================================================================
// Deadline List
// ============================================================================

function buildDeadlineListPath(params: FilingDeadlineListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.status) searchParams.set('status', params.status);
  if (params.electionType) searchParams.set('electionType', params.electionType);
  return `/api/finance/deadlines?${searchParams.toString()}`;
}

/** A page of the campaign's filing deadlines, soonest due first. */
export const useFinanceDeadlines = defineQuery({
  key: (params: FilingDeadlineListParams) => ['finance', 'deadlines', 'list', params],
  reads: 'GET /api/finance/deadlines',
  fetch: (params, { signal, campaignId }) =>
    apiJson<DeadlineListResponse>(buildDeadlineListPath(params), {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch deadlines',
    }),
  staleTime: FINANCE_STALE_TIME,
});

// ============================================================================
// Create Manual Reporting Period (#132 / #121)
//
// POST /api/finance/deadlines/manual — the treasurer hand-enters a coverage
// window + due date + optional report code for a jurisdiction whose calendar the
// app can't fully auto-generate (VA's election-relative periods). The server
// validates contiguity + non-overlap and returns a `ManualPeriod*` error code on a
// 400 (`code`, beside the sentence in `error`); the hook surfaces it as `CreateManualDeadlineError.code` so the form maps
// it to treasurer copy via `manualDeadlineErrorCopy`.
// ============================================================================

/** A manual-deadline validation refusal (400) carrying its `ManualPeriod*` error code. */
export class CreateManualDeadlineError extends Error {
  constructor(public readonly code: string, message?: string) {
    super(message ?? code);
    this.name = 'CreateManualDeadlineError';
  }
}

const useCreateManualDeadlineWrite = defineWrite({
  // The period is always created on 200; the envelope also carries a non-blocking
  // `reportCodeWarning` (#220) the form surfaces without failing the create.
  send: (request: CreateManualDeadlineRequest): Promise<CreateManualDeadlineResponse> =>
    apiJson<CreateManualDeadlineResponse>('/api/finance/deadlines/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: request,
      // Carry the server's wire code so the form renders `manualDeadlineErrorCopy`
      // — never a raw HTTP failure or a hardcoded string. The body can be `null`.
      error: (body: ApiErrorBody | null) =>
        new CreateManualDeadlineError(body?.code ?? 'ManualPeriodUnknown', body?.error),
    }),
});

export function useCreateManualDeadline() {
  const { mutateAsync } = useCreateManualDeadlineWrite();
  return { createManualDeadline: mutateAsync };
}

// ============================================================================
// Recognized report types (#220) — jurisdiction reference data driving the
// manual reporting-period code picker (code + treasurer-facing label). Empty
// for a jurisdiction that publishes no code list (VA), so the field degrades to
// a plain free-text box. Keyed by ruleset code (global reference data, not
// campaign-scoped); only fetched once a ruleset is known.
// ============================================================================

export function useRecognizedReportTypes(rulesetCode: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.finance.rulesets.reportTypes(rulesetCode ?? ''),
    queryFn: () =>
      apiJson<RecognizedReportTypeDto[]>(`/api/finance/rulesets/${rulesetCode}/report-types`, {
        error: 'Failed to fetch recognized report types',
      }),
    enabled: authService.isAuthenticated() && !!rulesetCode,
    staleTime: FINANCE_STALE_TIME,
  });
}

// ============================================================================
// Health Bar
// ============================================================================

export const useFinanceHealthRead = defineQuery({
  key: financeReadKeys.health,
  reads: 'GET /api/finance/health',
  fetch: (_params: undefined, { signal, campaignId }) =>
    apiJson<FinanceHealthDto>('/api/finance/health', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch finance health',
    }),
  staleTime: FINANCE_STALE_TIME,
});

export function useFinanceHealth(): CurrentRead<FinanceHealthDto> {
  return useFinanceHealthRead(undefined);
}

// ============================================================================
// Obligation Bar (FIP-002)
// ============================================================================

const useObligationsRead = defineQuery({
  key: financeReadKeys.obligations,
  reads: 'GET /api/finance/obligations',
  fetch: (_params: undefined, { signal, campaignId }) =>
    apiJson<ObligationsResponse>('/api/finance/obligations', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch obligations',
    }),
  staleTime: FINANCE_STALE_TIME,
});

export function useObligations(): CurrentRead<ObligationsResponse> {
  return useObligationsRead(undefined);
}
